import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { axiosInstance } from '@/utils/axios';

import { mapTimeTrackerProjectsToDropdownItems } from '../mappers/time-tracker-project.mapper';
import {
  FindTimeTrackerProjectsFilterRequest,
  FindTimeTrackerProjectsRequest,
} from '../types/dto/request/find-time-tracker-projects-request';
import { TimeTrackerProjectResponse } from '../types/dto/response/time-tracker-project-response';

/**
 * Fetches projects for the Time Tracking project picker, sorted with the
 * member's booked projects first (see `hasRelevantAssignments` on each item).
 *
 * @param params - Filter (including the booking check window) and pagination parameters
 * @returns Paginated list of projects as DropdownSearchItem, booked projects first
 */
export const getTimeTrackerProjects = async (params: {
  filter: FindTimeTrackerProjectsFilterRequest;
  pagination?: { cursor?: number | null; pageSize?: number };
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  const endpoint = '/api/projects/time-tracker/search';

  const requestBody: FindTimeTrackerProjectsRequest = {
    filter: params.filter,
    pagination: {
      pageSize: params.pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      cursor: params.pagination?.cursor,
    },
  };

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.PROJECTS_LOAD_FAILED, () =>
    axiosInstance.post<
      ApiResponse<{
        items: TimeTrackerProjectResponse[];
        pagination: { total: number; cursor: number | null };
      }>
    >(endpoint, requestBody)
  );

  return new PaginatedResponse({
    items: mapTimeTrackerProjectsToDropdownItems(response.items),
    pagination: {
      total: response.pagination.total,
      cursor: response.pagination.cursor,
    },
  });
};
